// Fonts for text added in Edit PDF (FR-13). The first three are the PDF's
// built-in fonts (nothing to download); the rest are free Google fonts
// served from this site (public/fonts/), fetched only when someone picks one.

export type FontGroup =
  "Built-in" | "Sans" | "Serif" | "Display" | "Handwriting" | "Mono";

export type FontInfo = {
  id: string;
  label: string;
  group: FontGroup;
  /** CSS font-family for the editor screen. */
  css: string;
  /** Font file under public/, or undefined for a built-in PDF font. */
  file?: string;
};

const google = (
  id: string,
  label: string,
  group: FontGroup,
  fallback: string,
): FontInfo => ({
  id,
  label,
  group,
  css: `"PDF ${label}", ${fallback}`,
  file: `/fonts/${id}.ttf`,
});

export const FONTS = [
  {
    id: "sans",
    label: "Helvetica",
    group: "Built-in",
    css: "Helvetica, Arial, sans-serif",
  },
  {
    id: "serif",
    label: "Times",
    group: "Built-in",
    css: "'Times New Roman', Times, serif",
  },
  {
    id: "mono",
    label: "Courier",
    group: "Built-in",
    css: "'Courier New', Courier, monospace",
  },
  google("roboto", "Roboto", "Sans", "sans-serif"),
  google("open-sans", "Open Sans", "Sans", "sans-serif"),
  google("lato", "Lato", "Sans", "sans-serif"),
  google("montserrat", "Montserrat", "Sans", "sans-serif"),
  google("poppins", "Poppins", "Sans", "sans-serif"),
  google("inter", "Inter", "Sans", "sans-serif"),
  google("nunito", "Nunito", "Sans", "sans-serif"),
  google("raleway", "Raleway", "Sans", "sans-serif"),
  google("work-sans", "Work Sans", "Sans", "sans-serif"),
  google("merriweather", "Merriweather", "Serif", "serif"),
  google("playfair-display", "Playfair Display", "Serif", "serif"),
  google("lora", "Lora", "Serif", "serif"),
  google("pt-serif", "PT Serif", "Serif", "serif"),
  google("eb-garamond", "EB Garamond", "Serif", "serif"),
  google("libre-baskerville", "Libre Baskerville", "Serif", "serif"),
  google("oswald", "Oswald", "Display", "sans-serif"),
  google("bebas-neue", "Bebas Neue", "Display", "sans-serif"),
  google("anton", "Anton", "Display", "sans-serif"),
  google("lobster", "Lobster", "Display", "cursive"),
  google("dancing-script", "Dancing Script", "Handwriting", "cursive"),
  google("pacifico", "Pacifico", "Handwriting", "cursive"),
  google("caveat", "Caveat", "Handwriting", "cursive"),
  google("great-vibes", "Great Vibes", "Handwriting", "cursive"),
  google("sacramento", "Sacramento", "Handwriting", "cursive"),
  google("roboto-mono", "Roboto Mono", "Mono", "monospace"),
  google("source-code-pro", "Source Code Pro", "Mono", "monospace"),
] as const satisfies readonly FontInfo[];

export type FontId = (typeof FONTS)[number]["id"];

export const FONT_GROUPS: FontGroup[] = [
  "Built-in",
  "Sans",
  "Serif",
  "Display",
  "Handwriting",
  "Mono",
];

export function fontInfo(id: FontId): FontInfo {
  return FONTS.find((f) => f.id === id) ?? FONTS[0];
}

/** Scripts whose letters change shape or order when joined (Arabic, Hebrew,
 *  Indian scripts, Thai …). pdf-lib can't do that, so such text is saved as a
 *  picture of the text instead. */
export function needsShaping(text: string): boolean {
  for (const ch of text) {
    const code = ch.codePointAt(0)!;
    if (code >= 0x0590 && code <= 0x1dff) return true;
    if (code >= 0xa8e0 && code <= 0xabff) return true;
    if (code >= 0xfb1d && code <= 0xfeff) return true;
  }
  return false;
}
